import { Center, Spinner, Text } from '@chakra-ui/react';
import useQueryParam from 'hooks/shared/useQueryParam';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchOrdersQuery } from 'rtkQuery/sharespro/queries/orders';

import OrdersGrid from 'presentation/components/modules/orders/grid/OrdersGrid';
import AppCard from 'presentation/components/shared/card/AppCard';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import ErrorContainer from 'presentation/components/shared/ErrorContainer';
import ActivesPageLayout from 'presentation/layout/modules/actives/ActivesPageLayout';
import { Helmet } from 'react-helmet-async';
import { isValidOrderState, OrderState } from 'enums/modules/order/orderState';
import ButtonTabs from 'presentation/components/shared/tabs/ButtonTabs';
import SidebarRequestCard from 'presentation/components/modules/pe/shared/SidebarRequestCard';
import { useFetchHomeQuery } from 'rtkQuery/sharespro/queries/home';
import usePeriodQueryParam from 'hooks/modules/period/usePeriodQueryParam';
import RebalanceGrid from 'presentation/components/modules/orders/grid/RebalanceGrid';
import { useLocation } from 'react-router-dom';

const OrdersPage: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const initialTab = location.state?.tab || 'active';
  const [page, setPage] = useQueryParam<number>('page', {
    ifAbsent: 1,
    provider: (val) => parseInt(val),
  });
  const [period] = usePeriodQueryParam();
  const { data: homeData } = useFetchHomeQuery({ period });
  const rebalanceAccount = homeData
    ? homeData.accounts.items.find(
        (e) =>
          e.rebalancing !== null && e.rebalancing?.recommendations.length !== 0
      )
    : null;

  const [orderState, setOrderState] = useQueryParam<OrderState>('state', {
    ifAbsent: OrderState.Active,
    provider: (val) => (isValidOrderState(+val) ? +val : OrderState.Active),
  });

  const [isRebalancingTab, setIsRebalancingTab] = useState(
    initialTab === 'rebalance'
  );

  const { isFetching, data, error, refetch } = useFetchOrdersQuery({
    page,
    state: orderState,
  });

  const orderStates = [OrderState.Active, 'rebalance', OrderState.Archived];
  const orderStateText = {
    [OrderState.Active]: t('orderStateActive'),
    rebalance: t('Ребалансировка'),
    [OrderState.Archived]: t('orderStateArchived'),
  };

  let content;
  let wrapByCard = true;

  if (isRebalancingTab) {
    if (
      rebalanceAccount &&
      rebalanceAccount.rebalancing!.recommendations.length !== 0
    ) {
      content = (
        <RebalanceGrid
          recs={rebalanceAccount.rebalancing!.recommendations}
          createdAt={rebalanceAccount.rebalancing!.createdAt}
        />
      );
      wrapByCard = false;
    } else {
      content = <Text>{t('listIsEmpty')}</Text>;
    }
  } else {
    if (data) {
      if (data.items.length > 0) {
        content = (
          <OrdersGrid
            items={data.items}
            pageCount={data.pageCount}
            currentPage={data.currentPage}
            setPage={setPage}
            isFetching={isFetching}
          />
        );
        wrapByCard = false;
      } else {
        content = <Text>{t('listIsEmpty')}</Text>;
      }
    } else if (isFetching) {
      content = <Spinner />;
    } else if (error) {
      content = (
        <ErrorContainer
          error={error}
          onRetry={() => {
            refetch();
          }}
        />
      );
    } else {
      content = <Spinner />;
    }
  }

  if (wrapByCard) {
    content = (
      <AppCard p="20px">
        <Center>{content}</Center>
      </AppCard>
    );
  }

  return (
    <ActivesPageLayout sidebarContent={<SidebarRequestCard />}>
      <Helmet>
        <title>{t('myRecommendations')}</title>
      </Helmet>
      <GridHeading>{t('myRecommendations')}</GridHeading>
      <ButtonTabs
        mb="20px"
        onChange={(index) => {
          if (orderStates[index] === 'rebalance') {
            setIsRebalancingTab(true);
          } else {
            setIsRebalancingTab(false);
            setOrderState(orderStates[index] as OrderState);
          }
        }}
        index={isRebalancingTab ? 1 : orderStates.indexOf(orderState)}
        items={orderStates.map(
          (state) => orderStateText[state as keyof typeof orderStateText]
        )}
      />
      {content}
    </ActivesPageLayout>
  );
};

export default OrdersPage;
